export const SERVER_PORT = 4900;

export const CAPTAIN_NAME_MAX_LENGTH = 24;
export const CAPTAIN_NAME_MIN_LENGTH = 2;

// World coordinates are in kilometers. X+ = East, Y+ = South.
export const CADIZ_POS = { x: 0, y: 0 };
export const TORTUGA_POS = { x: -6500, y: 2000 };
export const GENOA_POS = { x: 1290, y: -880 };

export const CADIZ_BAY_RADIUS_KM = 25;

/** A voyage leg is complete once the ship is within this many km of the
 *  destination port's offshore anchorage (`Port.position`). */
export const PORT_ARRIVAL_RADIUS_KM = 25;

// World bounding box for entity spawning.
export const WORLD_X_MIN = -7400;
export const WORLD_X_MAX = 2500;
export const WORLD_Y_MIN = -2700;
export const WORLD_Y_MAX = 3500;

// ── Sailing simulation ──────────────────────────────────────────────────────

export const TICK_INTERVAL_MS = 60_000;
export const VOYAGE_SIM_STEP_SEC = 60;
export const VOYAGE_MAX_CATCHUP_STEPS = 50_000;
export const VOYAGE_LOG_LIMIT = 80;

// Ship & wind
/** Historically a Spanish galleon or English merchantman cruised at 4-5 knots in fair
 *  wind, with bursts to 7-8 in a strong following breeze. We model the per-step max
 *  with a directional cosine and wind-speed factor, so 5 here gives an average
 *  closer to 3-4 knots — period-realistic for a Cádiz → Tortuga run of 35-50 days. */
export const SHIP_MAX_SPEED_KNOTS = 5;

// Sail trim — player-controlled sail level. Multiplies the sail-driven speed
// from effectiveSpeedKnots() before the sails-damaged factor is applied.
// 'row' is special-cased in the sim (fixed speed, ignores wind), not driven by this table.
export const SAIL_TRIM_FACTOR: Record<'full' | 'half' | 'reefed' | 'anchor' | 'row', number> = {
  full: 1.0,
  half: 0.5,
  reefed: 0.25,
  anchor: 0.0,
  row: 0.0,
};

// Fishing yield multiplier by sail level. Lower sails → calmer ship → better
// rod and net catch rates. Applied multiplicatively to rod catch chance and
// net catch-per-hour. Harpoon is unaffected (it's an aimed throw at a target).
// Rowing is the worst — deck is busy with oars.
export const FISHING_SAIL_MULTIPLIER: Record<'full' | 'half' | 'reefed' | 'anchor' | 'row', number> = {
  full: 0.40,
  half: 0.65,
  reefed: 0.85,
  anchor: 1.0,
  row: 0.20,
};

// Rowing — manual propulsion for headwinds and doldrums.
// Sails are fully drawn in while rowing, so wind doesn't drive speed — but
// the rowers can only partially fight crosswind heading drift.
// Speed scales with hands at the oars: base + per-sailor bonus. A skeleton
// crew rows slower than the old fixed rate (5 × 0.35 = 1.75 kt); a full
// complement pulls well past it.
export const ROWING_BASE_SPEED_KNOTS = 1.5;
export const ROWING_SPEED_PER_SAILOR_KNOTS = 0.1;
/** Fraction of normal leeway felt while rowing. Oars hold most but not all course. */
export const ROWING_LEEWAY_FACTOR = 0.3;
/** Base morale lost per day while rowing — meaningful penalty for sustained rowing. */
export const ROWING_MORALE_DRAIN_BASE_PER_DAY = 6;
/** Extra morale drain per missing sailor below the "fair shift" threshold. */
export const ROWING_FAIR_SHIFT_CREW = 10;
export const ROWING_MORALE_DRAIN_PER_MISSING_CREW = 0.8;
export const KNOT_TO_KM_PER_HOUR = 1.852;
export const WIND_DOLDRUMS_THRESHOLD_KNOTS = 1.5;
export const WIND_GALE_THRESHOLD_KNOTS = 22;
export const WIND_STORM_THRESHOLD_KNOTS = 32;
export const WIND_DEFAULT_SPEED_KNOTS = 8;
export const WIND_DEFAULT_DIRECTION_RAD = Math.PI;
export const WIND_SHIFT_INTERVAL_HOURS = 3;

/**
 * Wind has a slight bias toward the historical trade-wind direction —
 * blowing toward WSW (≈ 2.6 rad in our internal angle system where 0=E, π=W,
 * +y=south). At each shift the direction is pulled fractionally back toward
 * this anchor and noise is added. Speed has its own random walk and storm
 * escalation; this is purely about direction.
 */
export const WIND_PREFERRED_DIRECTION_RAD = 2.6;
/** Fraction of the gap between current and preferred direction that is closed each shift. */
export const WIND_REVERSION_FACTOR_PER_SHIFT = 0.15;
/** ±radians of random direction jitter added on top of reversion at each shift. */
export const WIND_RANDOM_WALK_RAD = Math.PI / 6;

// ── Wind grid (regional weather) ────────────────────────────────────────────
/** Side length of one wind cell, in km. The world is tiled into a coarse grid
 *  and each cell evolves its own direction + speed. Two captains in the same
 *  cell experience the same wind. */
export const WIND_GRID_CELL_KM = 500;
/** Inclusive bounds of the wind grid in world coords. */
export const WIND_GRID_X_MIN = -9500;
export const WIND_GRID_X_MAX = 2500;
export const WIND_GRID_Y_MIN = -2700;
export const WIND_GRID_Y_MAX = 3500;
/** Each tick, blend a fraction of each cell's wind toward the average of its
 *  four neighbours, so wind regions stay correlated and don't pixelate. */
export const WIND_GRID_NEIGHBOR_BLEND = 0.18;
/** Fraction of the gap between cell direction and its latitude climatology
 *  that is closed each tick. Higher = climatology dominates more strongly. */
export const WIND_GRID_CLIMATOLOGY_PULL = 0.05;
/** Per-tick wind speed jitter (knots) per cell. */
export const WIND_GRID_SPEED_JITTER = 0.6;
/** Per-tick wind direction jitter (radians) per cell. */
export const WIND_GRID_DIR_JITTER = Math.PI / 24;
export const LEEWAY_MAX_RAD = 0.25;
export const WIND_DRIFT_FACTOR = 0.03;
/**
 * Per-captain fixed heading bias — every ship is built a little different and
 * tracks slightly off the set heading. Sampled once per captain at create time
 * in [-MAX, +MAX]. Two ships on identical headings will diverge over distance.
 */
export const HEADING_BIAS_MAX_RAD = 0.04;

// Crew consumption
export const FOOD_RATIONS_PER_SAILOR_DAY = 1;
export const WATER_RATIONS_PER_SAILOR_DAY = 1;
export const ANTISCURVY_RATIONS_PER_SAILOR_DAY = 1;

export const ITEM_YIELDS: Record<string, { food?: number; water?: number; antiscurvy?: number }> = {
  hardtack:     { food: 5 },
  salted_pork:  { food: 8 },
  dried_beans:  { food: 6 },
  fresh_water:  { water: 7 },
  lemons:       { antiscurvy: 30 },
  sauerkraut:   { antiscurvy: 30 },
  lime_juice:   { antiscurvy: 60 },
  whale_meat:   { food: 10 },
  fish_caught:  { food: 2 },
};

export const SCURVY_GRACE_DAYS = 40;
export const SCURVY_DEATH_INTERVAL_DAYS = 6;
export const STARVATION_DEATH_INTERVAL_DAYS = 3;
export const THIRST_DEATH_INTERVAL_DAYS = 2;

export const STORM_INTERVAL_HOURS = 24 * 20;

// ── Hurricane season ────────────────────────────────────────────────────────
/** During real-world months in this set, hurricanes can spawn in the belt. 0=Jan, 5=Jun. */
export const HURRICANE_SEASON_MONTHS = new Set([5, 6, 7, 8, 9, 10]); // Jun–Nov inclusive
/** AABB defining the basin where hurricanes form. Real Atlantic hurricanes
 *  spawn off W. Africa and tracks NW across the Caribbean / Gulf. */
export const HURRICANE_BELT = { x0: -7400, x1: -1000, y0: 600, y1: 2700 };
/** Spawn rolls per real hour while at least one captain is at sea, in season + belt.
 *  Tuned so we get roughly the real Atlantic count of ~7 hurricanes + ~7 tropical
 *  storms across the 6-month season. */
export const HURRICANE_SPAWN_PER_HOUR = 1 / (24 * 12);
/** Probability that a fresh weather system is a true hurricane (vs a tropical storm). */
export const HURRICANE_TRUE_PROB = 0.30;
/** Damage multiplier for true hurricanes (not generic tropical storms). */
export const HURRICANE_STORM_DAMAGE_MULT = 1.4;
export const STORM_HULL_DAMAGE_PCT = 16;
export const GALE_HULL_DAMAGE_PCT_PER_DAY = 1;

// ── Hurricane / tropical storm entities ─────────────────────────────────────
/** Wind cell intensity at the eye of a true hurricane (kt). */
export const HURRICANE_EYE_WIND_KNOTS = 38;
/** Wind cell intensity at the centre of a tropical storm (kt). */
export const TROPICAL_STORM_WIND_KNOTS = 28;
/** Radius of the storm's wind influence on the grid + visibility on chart. */
export const HURRICANE_RADIUS_KM = 220;
/** Drift direction (toward NW, our coords: -x, -y → atan2(-1, -1) = -3π/4). */
export const HURRICANE_DRIFT_HEADING = -3 * Math.PI / 4;
/** Drift speed of the storm system itself (not the wind around it). */
export const HURRICANE_DRIFT_KNOTS = 8;
/** Lifetime range for a hurricane (real hours). */
export const HURRICANE_LIFETIME_HOURS_MIN = 72;
export const HURRICANE_LIFETIME_HOURS_MAX = 168;
/** Per-step probability of a captain inside a hurricane taking heavy damage. */
export const HURRICANE_DAMAGE_INTERVAL_HOURS = 4;

// ── Fog bank entities ───────────────────────────────────────────────────────
/** Spawn rate of new fog banks per real hour, anywhere on the map. */
export const FOG_BANK_SPAWN_PER_HOUR = 1 / 6;
/** Radius of a fog bank's effective cover (km). Captains within this become fogged. */
export const FOG_BANK_ENTITY_RADIUS_KM = 60;
/** Drift speed of fog banks (slow, blown by local wind). */
export const FOG_BANK_DRIFT_KNOTS = 1.2;
/** Fog banks dissipate after this many real hours. */
export const FOG_BANK_LIFETIME_HOURS_MIN = 6;
export const FOG_BANK_LIFETIME_HOURS_MAX = 14;
/** Soft cap on simultaneous fog banks worldwide. */
export const FOG_BANK_MAX_ACTIVE = 12;

// ── Coastlines ──────────────────────────────────────────────────────────────
/** Within this many km of a landmass, the at-sea screen will show "coast in sight". */
export const COAST_VISIBILITY_KM = 80;

// ── Sighting & chat ─────────────────────────────────────────────────────────
export const VISUAL_RANGE_NAKED_EYE_KM = 12;
export const VISUAL_RANGE_TELESCOPE_KM = 35;
export const SIGHTING_LIMIT = 12;

// ── Fog banks ───────────────────────────────────────────────────────────────
/** While fogged, the lookout's range collapses to this regardless of telescope. */
export const FOG_BANK_RANGE_KM = 3;
/** Probability per simulated day that a captain rolls into a fog bank. */
export const FOG_BANK_PROB_PER_DAY = 0.05;
/** Fog bank lifts after a random duration in this range (game-hours). */
export const FOG_BANK_DURATION_HOURS_MIN = 6;
export const FOG_BANK_DURATION_HOURS_MAX = 14;

// ── Fog of war ──────────────────────────────────────────────────────────────
/** Side length of one explored cell, in km. Smaller = finer map, more rows in DB. */
export const FOG_CELL_SIZE_KM = 5;

export const CHAT_VOICE_RANGE_KM = 1;
export const CHAT_SEMAPHORE_RANGE_KM = 8;
export const CHAT_FLAG_RANGE_KM = 20;
export const CHAT_SMOKE_RANGE_KM = 50;
export const CHAT_MAX_LENGTH = 200;

export const GARBLE_VOICE_RATE = 0;
export const GARBLE_SEMAPHORE_RATE = 0.10;
export const GARBLE_FLAGS_RATE = 0.40;
export const GARBLE_SMOKE_RATE = 0.65;

// ── Entities ───────────────────────────────────────────────────────────────
export const ENTITY_AI_SHIP_COUNT = 30;
export const ENTITY_WHALE_COUNT = 110;
export const ENTITY_FISH_SCHOOL_COUNT = 280;

export const WHALE_BASE_HP = 4;
export const WHALE_FLEE_SPEED_KNOTS = 4;
export const WHALE_CALM_SPEED_KNOTS = 0.6;
export const WHALE_REWARD_REALES = 20;
export const WHALE_REWARD_MEAT_UNITS = 5;

export const AI_SHIP_BASE_HP = 12;
export const AI_SHIP_CALM_SPEED_KNOTS = 1.5;
export const FISH_SCHOOL_DRIFT_SPEED_KNOTS = 0.4;

// ── Hostile pirates ─────────────────────────────────────────────────────────
// Rare hunters that haunt island waters and the American seaboard — never
// European or African coasts. They chase any captain who strays into range,
// but are rigged to be survivable: slightly slower than an average player
// vessel (so you can outrun them under sail, or at the oars with a good
// crew), long reload between volleys, small chip damage per volley, and a
// hard cap on the damage a single chase can inflict — one pirate can maul a
// healthy ship but never sink her outright. A ship already near the waterline
// is another story.
/** Spawn rolls per real hour (checked each world tick). ≈ one new pirate every 1.5 days. */
export const PIRATE_SPAWN_PROB_PER_HOUR = 1 / 36;
/** Hard cap on pirates at large across the whole world. */
export const PIRATE_MAX_ACTIVE = 5;
export const PIRATE_BASE_HP = 18;
/** Chase speed — a shade under the ~3-4 kt a player averages in fair wind. */
export const PIRATE_CHASE_SPEED_KNOTS = 2.6;
/** Loitering speed while patrolling their hunting ground. */
export const PIRATE_PATROL_SPEED_KNOTS = 1.0;
/** A pirate takes up the chase when a captain closes within this range. */
export const PIRATE_AGGRO_RANGE_KM = 20;
/** The chase is abandoned once the quarry opens the gap past this. */
export const PIRATE_GIVE_UP_RANGE_KM = 30;
export const PIRATE_CANNON_RANGE_KM = 4;
/** Reload between pirate volleys — long, so a chase is a running fight, not a massacre. */
export const PIRATE_VOLLEY_COOLDOWN_MS = 10 * 60_000;
/** Chance a pirate volley falls into the sea entirely. */
export const PIRATE_VOLLEY_MISS_CHANCE = 0.35;
/** Hull damage per volley that lands, rolled in [MIN, MAX]. */
export const PIRATE_VOLLEY_HULL_DAMAGE_MIN = 3;
export const PIRATE_VOLLEY_HULL_DAMAGE_MAX = 7;
/** Chance a landing volley also kills one named crewman. */
export const PIRATE_VOLLEY_CREW_KILL_CHANCE = 0.15;
/** Total hull damage one pirate will deal to one target before sheering off —
 *  guarantees a single encounter can never sink a healthy (100%) hull. */
export const PIRATE_CHASE_DAMAGE_CAP = 30;
/** After breaking off (escaped or satisfied), the pirate ignores that captain this long. */
export const PIRATE_REAGGRO_COOLDOWN_MS = 12 * 3600_000;
/** Pirates spawn on a ring this far from their anchor (an island or American coastal town). */
export const PIRATE_SPAWN_RING_KM_MIN = 15;
export const PIRATE_SPAWN_RING_KM_MAX = 60;
/** Spawns prefer anchors within this range of an at-sea captain, so the rare
 *  spawn has someone to menace; failing that, anywhere qualifying. */
export const PIRATE_SPAWN_NEAR_PLAYER_KM = 250;
/** Coastal towns west of this longitude count as the Americas (pirate waters);
 *  towns east of it are Europe / Africa and are never used as spawn anchors. */
export const PIRATE_AMERICAS_X_MAX = -3000;
/** Pirates sail off to other hunting grounds after this long (real hours). */
export const PIRATE_LIFETIME_HOURS_MIN = 48;
export const PIRATE_LIFETIME_HOURS_MAX = 120;

// ── Fishing ─────────────────────────────────────────────────────────────────
export const HARPOON_COOLDOWN_MS = 5 * 60_000;
export const HARPOON_BASE_HIT_CHANCE = 0.55;
export const HARPOON_GUNNER_HIT_BONUS = 0.2;
export const HARPOON_CRIT_CHANCE = 0.1;
export const HARPOON_MAX_RANGE_KM = 5;
export const HARPOON_LOSS_ON_MISS = 0.5;

// ── Whale tether (Nantucket sleigh ride) ────────────────────────────────────
// A harpoon's job is to STICK the whale to the ship, not to kill it. The
// whale flees, towing the ship behind, the line wears it out, and when the
// whale is exhausted the captain finishes it with a lance to the lungs.
/** Whale tow speed while at panic — historical sleigh rides hit 15+ knots; we cap
 *  somewhere above ship max so the player can feel the difference but not run away. */
export const WHALE_PANIC_SPEED_KNOTS = 7;
/** Tow speed when the whale is fully exhausted — a crawl, perfect for the lance. */
export const WHALE_EXHAUSTED_SPEED_KNOTS = 0.3;
/** Rope length — ship sits this far behind the whale's position while towed (km). */
export const TETHER_LINE_LENGTH_KM = 0.08;
/** Fatigue points gained per minute, per harpoon in. Tuned so 1 harpoon ≈ 25 min,
 *  2 harpoons ≈ 12 min, 3 harpoons ≈ 8 min to exhaust. */
export const TETHER_FATIGUE_PER_HARPOON_PER_MIN = 4;
/** Per-minute chance the whale sounds (dives) and tears free one harpoon.
 *  If it's the last harpoon, the line breaks. */
export const TETHER_SOUND_PROB_PER_MIN = 0.012;
/** Per-minute chance the full tether line snaps regardless of harpoon count.
 *  Reduced by harpoonsIn (multiple lines = redundancy). */
export const TETHER_LINE_BREAK_PROB_PER_MIN = 0.004;
/** Fatigue threshold above which the lance becomes lethal in one thrust. */
export const TETHER_EXHAUSTED_FATIGUE = 95;
/** Range within which a lance thrust will land. Close quarters work. */
export const WHALE_LANCE_RANGE_KM = 0.5;
/** Fatigue added per successful musket shot from the deck. */
export const MUSKET_SHOT_FATIGUE = 10;
/** Cooldown between musket shots at a tethered whale (powder + ball each shot). */
export const MUSKET_SHOT_COOLDOWN_MS = 30_000;
/** Musket hit chance from a moving deck onto a thrashing whale. */
export const MUSKET_SHOT_HIT_CHANCE = 0.5;

export const ROD_COOLDOWN_MS = 2 * 60_000;
export const ROD_BASE_CATCH_CHANCE = 0.4;
export const ROD_NEAR_SCHOOL_BONUS = 0.4;
export const ROD_BAIT_BONUS = 0.2;

export const NET_BREAK_CHANCE_PER_TICK = 0.005;
export const NET_GALE_BREAK_MULT = 8;
export const NET_BASE_CATCH_PER_HOUR = 1.5;
export const NET_NEAR_SCHOOL_CATCH_MULT = 4;
export const NEAR_SCHOOL_RANGE_KM = 1.5;

// ── Combat ──────────────────────────────────────────────────────────────────
export const CANNON_VOLLEY_COOLDOWN_MS = 90_000;
export const CANNON_6LB_RANGE_KM = 3;
export const CANNON_12LB_RANGE_KM = 5;
export const CANNON_BASE_HIT_CHANCE = 0.4;
export const CANNON_GUNNER_HIT_BONUS = 0.2;
export const CANNON_CRIT_CHANCE = 0.05;
export const CANNON_CRIT_MULT = 1.6;
/** Hit chance is multiplied by (1 - r * RANGE_FALLOFF) where r = distance / max_range. */
export const CANNON_RANGE_FALLOFF = 0.6;
export const CANNON_POWDER_PER_VOLLEY = 1;

/** Damage tables: shot type × cannon size → hull/crew/sail effects. */
export const SHOT_DAMAGE: Record<'round' | 'grape' | 'chain', { hull6: number; hull12: number; crewKillChance: number; sailDamageHours: number }> = {
  round: { hull6: 8, hull12: 14, crewKillChance: 0,    sailDamageHours: 0 },
  grape: { hull6: 2, hull12: 3,  crewKillChance: 0.45, sailDamageHours: 0 },
  chain: { hull6: 1, hull12: 2,  crewKillChance: 0.05, sailDamageHours: 1 },
};

/** When chain shot lands, the target's sail speed is reduced for this fraction. */
export const SAILS_DAMAGED_SPEED_FACTOR = 0.3;

// ── Trade ──────────────────────────────────────────────────────────────────
export const TRADE_RANGE_KM = 2;
export const TRADE_OFFER_TTL_MS = 5 * 60_000;

// ── Boarding ───────────────────────────────────────────────────────────────
/** Within this many km of another captain, you can grapple and board. */
export const BOARDING_RANGE_KM = 2;
/** Combat power per weapon type. Capped by available crew (one man per arm). */
export const BOARDING_POWER_PER_SAILOR  = 1.0;
export const BOARDING_POWER_PER_CUTLASS = 0.5;
export const BOARDING_POWER_PER_PISTOL  = 1.0;
export const BOARDING_POWER_PER_MUSKET  = 1.5;
/** Master gunner organises the boarding party — multiplicative bonus to power. */
export const BOARDING_GUNNER_BONUS_MULT = 1.3;
/** Fraction of the winner's crew that dies, scaled by loser_power / winner_power. */
export const BOARDING_WIN_LOSS_FACTOR = 0.4;
/** Hard cap on winner's casualties so a close fight doesn't wipe both sides. */
export const BOARDING_MAX_WIN_LOSS_FRACTION = 0.7;

// ── Morale ──────────────────────────────────────────────────────────────────
export const MORALE_START = 70;
export const MORALE_MAX = 100;
export const MORALE_DECAY_PER_DAY = 2;
export const MORALE_RUM_BONUS = 10;
export const MORALE_CAT_BONUS = 8;
export const MORALE_SUNRISE_BONUS = 3;
export const MORALE_SUNSET_BONUS = 4;
export const MORALE_STORM_HIT = 8;
export const MORALE_DOLDRUMS_PER_DAY = 3;
export const MORALE_DEATH_HIT = 10;
export const MORALE_CRIME_HIT = 15;
export const MUTINY_PROB_PER_DAY = 0.25;

export const RUM_RATION_INTERVAL_DAYS = 3;
export const SUNRISE_PROB_PER_DAY = 0.25;
export const SUNSET_PROB_PER_DAY = 0.25;
export const CAT_EVENT_PROB_PER_DAY = 0.12;

// Crime base/max rates — interpolated linearly by morale.
//   rate = base + (max - base) * (1 - morale / MORALE_MAX)
// Murder peaks at 1%/day at morale=0; theft at 2%/day. At full morale (100), crimes
// are rare. Each roll happens once per simulated day per voyage.
export const CRIME_MURDER_BASE_PROB_PER_DAY = 0.0005;
export const CRIME_MURDER_MAX_PROB_PER_DAY  = 0.01;
export const CRIME_THEFT_BASE_PROB_PER_DAY  = 0.001;
export const CRIME_THEFT_MAX_PROB_PER_DAY   = 0.02;
/** Per loose flagged criminal, per day — chance they re-offend if not in irons. */
export const CRIME_REPEAT_PROB_PER_DAY = 0.20;
export const BRIG_MORALE_DRAIN_PER_DAY = 1;

// Brig
export const BRIG_CAPACITY = 3;
/** Per in-brig crew member, per simulated day — chance the irons have worked their lesson. */
export const BRIG_REHAB_PROB_PER_DAY = 0.08;
/** Morale hit when imprisoning an innocent crew member (no crime flag). */
export const UNJUST_BRIG_MORALE_HIT = 5;

// Manual crew actions (player buttons on the Crew card)
export const RATION_RUM_COOLDOWN_MS   = 4 * 3600_000;
export const RATION_RUM_MORALE_BONUS  = 10;
export const RATION_RUM_ACCIDENT_PROB = 0.01;
export const SWIM_COOLDOWN_MS         = 4 * 3600_000;
export const SWIM_MORALE_BONUS        = 12;
export const SWIM_ACCIDENT_PROB       = 0.01;

// ── Crew interactions ───────────────────────────────────────────────────────
// Once per simulated day the crew produces one bit of scuttlebutt for the log.
// Almost always harmless colour; a small slice of interactions are two men
// conspiring — theft of rations, or (rarer) a killing. Conspirators are flagged
// with the ordinary crime machinery (brig, rehab, repeat offenses all apply).
// NOTE: rolled per sim step as rate*dtDays with a one-per-day cap, so the
// effective daily chance is 1 − e^−rate. 2.2 → ≈ 0.89: once a day, most days.
export const CREW_INTERACTION_PROB_PER_DAY = 2.2;
/** Fraction of interactions that are a two-man plot to steal rations (≈ every other voyage). */
export const CONSPIRACY_THEFT_PROB = 0.01;
/** Fraction of interactions that are a two-man plot to kill a shipmate (≈ 1 in 6 voyages). */
export const CONSPIRACY_MURDER_PROB = 0.003;

// ── Syphilis ("the French disease") ─────────────────────────────────────────
/** Per new hire signed at the dock — chance he comes aboard already poxed. */
export const SYPHILIS_STARTING_PROB = 0.01;
/** Per island shore leave (weighing anchor after trading) — chance one sailor picks it up. */
export const SYPHILIS_ISLAND_PROB = 0.15;
/** Per infected crewman per day — chance he passes it to a shipmate. */
export const SYPHILIS_SPREAD_PROB_PER_DAY = 0.01;
export const SYPHILIS_METER_MAX = 100;
/** Days for the meter to climb from fresh infection (1) to the maximum. */
export const SYPHILIS_METER_DAYS = 30;
/** At max meter the sailor is in syphilitic insanity — daily crime rolls below.
 *  At most ONE insanity crime happens per ship per simulated day, and madmen are
 *  excluded from the normal repeat-crime engine (madness answers to no bosun). */
export const SYPHILIS_INSANITY_THEFT_PROB_PER_DAY = 0.25;
export const SYPHILIS_INSANITY_MURDER_PROB_PER_DAY = 0.05;

// ── Item effects ────────────────────────────────────────────────────────────
/** Multiplier applied to the scurvy death interval when surgeon + medicine_chest are aboard. */
export const SURGEON_SCURVY_INTERVAL_MULT = 2;
export const CARPENTER_HULL_REPAIR_PCT_PER_DAY = 0.5;
/** Hull integrity restored per bundle of lumber when the captain orders a manual patch. */
export const HULL_REPAIR_PCT_PER_LUMBER = 12;
export const SEXTANT_LEEWAY_FACTOR = 0.5;
export const CHARTS_COAST_WARN_KM = 20;
export const MUSKET_CASUALTY_REDUCTION = 0.5;

// ── Islands ────────────────────────────────────────────────────────────────
export const ISLAND_LANDING_RANGE_KM = 3;
/** Within this distance of an island's centre, the ship has hit the rocks. */
export const ISLAND_GROUNDING_RADIUS_KM = 0.6;

// ── Tool breakage ───────────────────────────────────────────────────────────
/** Daily probability that a key shipboard tool snaps / cracks / spoils, removing it
 *  from the captain's inventory until they can replace it at an island shop. */
export const TOOL_BREAKAGE_PER_DAY: Record<string, { prob: number; label: string }> = {
  carpenters_tools: { prob: 0.012, label: "the carpenter's tools" },
  fishing_rod:      { prob: 0.012, label: 'the fishing rod' },
  sextant:          { prob: 0.004, label: 'the sextant' },
  telescope:        { prob: 0.004, label: 'the telescope glass' },
  sea_charts:       { prob: 0.004, label: 'the sea charts' },
  medicine_chest:   { prob: 0.005, label: 'the medicine chest stores' },
};
/** Per-size attack chance on landing at a tiny (size=1) island. */
export const ISLAND_SAVAGE_CHANCE = 0.5;
export const ISLAND_SHOP_MARKUP_BY_SIZE: Record<number, number> = {
  1: 0,    // no shop
  2: 1.4,  // 40% over Cádiz prices
  3: 1.2,  // 20% over
  4: 1.0,  // standard
};
/** Fraction of standard price an island will pay you for items you sell. */
export const ISLAND_SELL_FRACTION_BY_SIZE: Record<number, number> = {
  1: 0,
  2: 0.5,
  3: 0.6,
  4: 0.7,
};

// ── Shipwrecks ──────────────────────────────────────────────────────────────
/** Fraction of a lost captain's inventory saved in the wreck. */
export const SHIPWRECK_LOOT_FRACTION = 0.4;
/** Radius within which a passing ship discovers and auto-salvages a wreck. */
export const SHIPWRECK_RANGE_KM = 2;
/** Wrecks expire after this many ms (30 days). */
export const SHIPWRECK_TTL_MS = 30 * 24 * 3600_000;

// ── Off-course warning ──────────────────────────────────────────────────────
/** Days at sea before crew starts worrying about being off course. */
export const OFF_COURSE_WARN_DAYS = 40;
/** If still more than this many km from Tortuga after OFF_COURSE_WARN_DAYS, crew warns. */
export const OFF_COURSE_DIST_KM = 5500;
